/*
 * TunePad
 * Northwestern University
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { Note } from "./note";
import { SynthPatchRef } from "./synth";
import { toInt } from "./utils";

/**
 * A Trace object is generated by the python compiler and represents a list of
 * audio events that generate music.
 * Traces are immutable.
 */
export class MusicTrace {

    /// a trace is just a list of events
    private trace = new Array<TraceEvent>();

    /// allows for iteration through the events
    *[Symbol.iterator](): Iterator<TraceEvent> {
        for (const item of this.trace) {
            yield item;
        }
    }

    /// number of events in the trace
    public get length(): number { return this.trace.length; }

    /// is this trace empty
    public get isEmpty(): boolean { return this.length === 0; }

    /// number of beats in the trace (to the end of the last note or rest)
    private _beats: number = 0;
    public get beats(): number { return this._beats; }

    /// current position of the playhead as the trace is being constructed
    private _playhead: number = 0;

    /// what's the minimum note value in this trace (or -1 if there are no notes)
    private _minNote: number = -1;
    public get minNote(): number { return this._minNote; }
    public get minOctave(): number { return Math.max(0, Math.floor(this._minNote / 12)); }

    /// what's the maximum note value in this trace (or -1 if there are no notes)
    private _maxNote: number = -1;
    public get maxNote(): number { return this._maxNote; }
    public get maxOctave(): number { return Math.max(0, Math.floor(this._maxNote / 12)); }

    /// what's the range between the highest and lowest note values
    public get noteRange(): number { return (this.maxNote - this.minNote); }
    public get octaveRange(): number { return (this.maxOctave - this.minOctave); }

    /// how many notes in this trace?
    private _noteCount: number = 0;
    public get noteCount(): number { return this._noteCount; }

    /** set of voice IDs in the trace */
    public readonly voices = new Set<number>();

    /// return only message events
    public get messages() { return this.trace.filter(t => t.params.get('type') === 'message'); }

    /// return only warning events
    public get warnings() { return this.trace.filter(t => t.params.get('type') === 'warning'); }

    /// return only unit test events
    public get unitTests() { return this.trace.filter(t => ['pass', 'fail'].includes(t.params.get('type'))); }

    /// return only unit tests that pass
    public get passes() { return this.trace.filter(t => t.params.get('type') === 'pass'); }

    /// return only unit tests that fail
    public get fails() { return this.trace.filter(t => t.params.get('type') === 'fail'); }

    /** apply a custom filter to the trace list */
    public filter(f : (e : TraceEvent) => boolean) { return MusicTrace.fromTrace(this.trace.filter(f), this.patch); }

    /** apply a custom sort to the trace list */
    public sort(f : (a : TraceEvent, b : TraceEvent) => number) { this.trace.sort(f); return this; }

    /** Return all events that intersect with a given playhead position */
    public slice(playhead : number) { return this.trace.filter(e => e.time <= playhead && e.end > playhead); }


    /// return the optional synthesizer patch associated with this trace
    public readonly patch? : SynthPatchRef;

    /** Creates an empty trace */
    constructor(patch ? : SynthPatchRef) {
        this.patch = patch;
    }


    /** Create an empty trace */
    public static emptyTrace(patch ? : SynthPatchRef) : MusicTrace {
        return new MusicTrace(patch);
    }

    /** Create a trace from python compiler output */
    public static fromPython(list: Array<Map<string, any>>, patch ? : SynthPatchRef) : MusicTrace {
        const trace = new MusicTrace(patch);
        for (const t of list) trace.addTraceEvent(TraceEvent.fromMap(t));
        return trace;
    }

    /** Clone the trace, possibly with a new synth patch */
    public static fromTrace(other : MusicTrace | TraceEvent[], patch ? : SynthPatchRef) : MusicTrace {
        const trace = new MusicTrace(patch);
        for (const e of other) trace.addTraceEvent(e.clone());
        return trace;
    }

    /** Join two traces together */
    public join(other : MusicTrace | TraceEvent[]) : MusicTrace {
        const trace = new MusicTrace(this.patch);
        for (const e of this.trace) trace.addTraceEvent(e.clone());
        for (const e of other) trace.addTraceEvent(e.clone());
        trace.sort((a, b) => a.time - b.time);
        return trace;
    }


    /**
     * Return a new trace will all events that have onsets greater than or equal to `start`
     * and less than `end`. Both parameters are beat values.
     */
    public subtrace(start : number, end : number) : MusicTrace {
        const result = new MusicTrace(this.patch);
        const epsilon = 0.000001;
        start -= epsilon;
        end -= epsilon;
        for (const e of this.trace) {
            if (e.time >= start && e.time < end) {
                result.addTraceEvent(e.clone());
            }
        }
        return result;
    }


    /**
     * Return a new trace with events of only the given types
     */
    public ofType(commands : string[]) : MusicTrace {
        const result = new MusicTrace(this.patch);
        for (const e of this.trace) {
            if (commands.includes(e.command)) {
                result.addTraceEvent(e.clone());
            }
        }
        return result;
    }


    /** 
     * Compare two trace objects to see if they are equivalent except for line numbers
     */
    public isEquivalent(other: MusicTrace): boolean {
        if (this.length !== other.length) return false;

        for (let i = 0; i < this.length; i++) {
            if (!this.trace[i].isEquivalent(other.trace[i])) return false;
        }
        return true;
    }


    private addTraceEvent(event: TraceEvent) {
        this.trace.push(event);
        switch (event.command) {
            case TraceEvent.PLAY:
                this._beats = Math.max(event.end, this._beats);
                this._playhead = event.end;
                if (this._minNote < 0 || event.note.note < this._minNote) {
                    this._minNote = event.note.note;
                }
                this._maxNote = Math.max(this._maxNote, event.note.note);
                this._noteCount++;
                break;

            case TraceEvent.SAMPLE:
                this._beats = Math.max(event.end, this._beats);
                this._playhead = event.end;
                this._noteCount++;
                break;

            case TraceEvent.REST:
                this._beats = Math.max(event.end, this._beats);
                this._playhead = event.end;
                break;
        }
        this.voices.add(event.voice);
    }

    /**
     * Return a list of events for the given trace group number
     */
    public getTraceGroup(bug: number): Array<TraceEvent> {
        return this.trace.filter((e) => e.trace_group === bug);
    }
}


/**
 * Audio trace of a program's execution. Trace events are generated by
 * python and then replayed to generate sounds and animation.
 */
export class TraceEvent {

    static readonly PLAY = "play";           /// command code to play a note
    static readonly SAMPLE = "sample";       /// command to play an audio sample from URL
    static readonly REST = "rest";           /// rest for a given number of beats
    static readonly PUSH_FX = "push_fx";     /// push an audio effect
    static readonly POP_FX = "pop_fx";       /// pop the audio effect stack
    static readonly MESSAGE = "message";

    /// used to generate unique trace event IDs
    private static _TRACE_ID = 0;

    /// unique trace event id
    public readonly id: number;

    /// name of the trace event
    public readonly command: string;

    /// start time of the event in beats
    public readonly time: number = 0;

    /// duration of the event in beats
    public duration: number = 1;

    /// end time of the event in beats
    public get end() { return this.time + this.duration; }

    /// python source line number of this command
    public line: number = -1;

    /// note to play (for playNote and playSound)
    public note = new Note(60);

    /// trace "group" that this event belongs to. all events 
    /// generated by the same python function call will have 
    /// matching trace ids.
    public trace_group: number = -1;

    /// every time fastforward, rewind, or moveTo is called a new voice is created
    public voice = 0;

    /// any additional parameters
    public params = new Map<string, any>();


    constructor(command: string, time: number, id? : number) {
        this.id = (id === undefined) ? TraceEvent._TRACE_ID++ : id;
        this.command = command;
        this.time = time;
    }


    public clone(): TraceEvent {
        let e = new TraceEvent(this.command, this.time, this.id);
        e.line = this.line;
        e.duration = this.duration;
        e.note = this.note.clone();
        e.trace_group = this.trace_group;
        e.voice = this.voice;
        for (let [key, value] of this.params) {
            e.params.set(key, value);
        }
        return e;
    }

    public static fromMap(m: Map<string, any>): TraceEvent {
        let e = new TraceEvent(String(m.get('command')), Math.max(0, Number(m.get('time'))));
        e.duration = Number(m.get('duration'));
        e.note.duration = e.duration;
        for (let [key, val] of m) {
            if (key === "note" && typeof val === 'number') {
                e.note.note = val;
            }
            else if (key === "pitch" && typeof val === 'number') {
                e.note.detune = val * 100;
            }
            else if (key === "playback" && typeof val === 'number') {
                e.note.rate = val;
            }
            else if (key === "velocity" && typeof val === 'number') {
                e.note.velocity = val;
            }
            else if (key === "sustain" && typeof val === 'number') {
                e.note.duration = Math.max(e.note.duration, val);
                //e.duration = e.note.duration;
            }
            else if (key === "line" && typeof val === 'number') {
                e.line = val;
            }
            else if (key === "trace") {
                e.trace_group = toInt(val, 0);
            }
            else if (key === "voice") {
                e.voice = toInt(val, 0);
            }
            else if (["command", "time", "duration"].includes(key)) {
                continue;
            }
            else {
                e.params.set(key, val);
            }
        }
        return e;
    }


    /**
     * Are two trace events the same except for their line numbers
     */
    public isEquivalent(other: TraceEvent): boolean {
        if (this.command === other.command &&
            this.time === other.time &&
            this.duration === other.duration &&
            this.note.isEqual(other.note)) {
            for (let [key, val] of this.params) {
                if (key !== 'line' && other.params.get(key) !== val) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }
}
